import type { DietPreference, DietTypes, Recipe } from '../types';

export const DIET_TYPE_KEYS = [
    'isKeto',
    'isVegetarian',
    'isVegan',
    'isGlutenFree',
    'isPaleo',
    'isLowCarb',
    'isDairyFree',
] as const satisfies readonly (keyof DietTypes)[];

export const DIET_TYPE_CONFIG: { key: keyof DietTypes; labelKey: string; emoji: string }[] = [
    { key: 'isKeto',       labelKey: 'isKeto',       emoji: '🥩' },
    { key: 'isVegetarian', labelKey: 'isVegetarian', emoji: '🥗' },
    { key: 'isVegan',      labelKey: 'isVegan',      emoji: '🌱' },
    { key: 'isGlutenFree', labelKey: 'isGlutenFree', emoji: '🌾' },
    { key: 'isPaleo',      labelKey: 'isPaleo',      emoji: '🦴' },
    { key: 'isLowCarb',    labelKey: 'isLowCarb',    emoji: '📉' },
    { key: 'isDairyFree',  labelKey: 'isDairyFree',  emoji: '🥛' },
];

/** Primary diet chips always shown in the recipe filter modal. */
export const PRIMARY_DIET_FILTER_KEYS = [
    'isKeto',
    'isVegetarian',
    'isVegan',
    'isGlutenFree',
    'isDairyFree',
    'isPaleo',
] as const satisfies readonly (keyof DietTypes)[];

export function hasExtendedDietFilterOptions(): boolean {
    return DIET_TYPE_CONFIG.length > PRIMARY_DIET_FILTER_KEYS.length;
}

export function toggleDietTypeFilter(
    current: readonly (keyof DietTypes)[],
    dietKey: keyof DietTypes,
): (keyof DietTypes)[] {
    return current.includes(dietKey)
        ? current.filter((key) => key !== dietKey)
        : [...current, dietKey];
}

export const DIET_PREFERENCES: DietPreference[] = [
    'none',
    'vegetarian',
    'keto',
    'vegan',
    'glutenFree',
    'paleo',
    'lowCarb',
    'dairyFree',
];

export const EMPTY_DIET_TYPES: DietTypes = {
    isKeto: false,
    isVegetarian: false,
    isVegan: false,
    isGlutenFree: false,
    isPaleo: false,
    isLowCarb: false,
    isDairyFree: false,
};

export function normalizeDietPreference(value: unknown): DietPreference {
    if (typeof value === 'string' && DIET_PREFERENCES.includes(value as DietPreference)) {
        return value as DietPreference;
    }
    return 'none';
}

export function normalizeDietTypes(value: unknown, legacyIsKeto?: boolean): DietTypes {
    const base = { ...EMPTY_DIET_TYPES };

    if (value && typeof value === 'object') {
        const source = value as Record<string, unknown>;
        for (const key of DIET_TYPE_KEYS) {
            base[key] = source[key] === true;
        }
    }

    if (legacyIsKeto === true) {
        base.isKeto = true;
    }

    return base;
}

/** Read keto flag from migrated dietTypes or legacy isKeto field. */
export function recipeIsKeto(recipe: Pick<Recipe, 'dietTypes' | 'isKeto'>): boolean {
    if (recipe.dietTypes) {
        return recipe.dietTypes.isKeto === true;
    }
    return recipe.isKeto === true;
}

export function resolveDietTypesForSave(
    currentUserId: string | undefined,
    ownerId: string | undefined,
    existing: DietTypes | undefined,
    next: DietTypes
): DietTypes {
    if (ownerId && currentUserId && ownerId !== currentUserId) {
        return existing ? { ...existing } : { ...EMPTY_DIET_TYPES };
    }
    return { ...next };
}

export function applyKetoToggle(dietTypes: DietTypes, isKeto: boolean): DietTypes {
    return { ...dietTypes, isKeto };
}
